import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import CONSTANTS from "../utils/constants";

export default function AdminCustomers() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("approved");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [actioningId, setActioningId] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", phone: "", email: "", password: "" });
  const [addLoading, setAddLoading] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);

  const showNotification = (type, text, duration = 3500) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), duration);
  };

  const hasPermission = (id) => userPermissions.includes(id);

  const fetchCustomers = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${CONSTANTS.baseRoute}/customers`, {
        params: status === "all" ? {} : { status },
      });
      setCustomers(data);
    } catch (error) {
      showNotification(
        "danger",
        error.response?.data?.error || t("customers.fetchError")
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const init = async () => {
      try {
        const userCookie = Cookies.get("user");
        if (!userCookie) return navigate("/admin");

        const user = JSON.parse(userCookie);
        if (!user?.id) return navigate("/admin");

        const { data: { permissions } } = await axios.get(
          `${CONSTANTS.baseRoute}/permissions`,
          { params: { user_id: user.id } }
        );
        setUserPermissions(permissions);

        if (!permissions.includes(36)) {
          setMessage({ type: "danger", text: t("customers.noPermissionView") });
          setTimeout(() => navigate("/admin/home"), 2500);
          return;
        }

        await fetchCustomers(statusFilter);
      } catch (error) {
        console.error("Error fetching customers:", error);
        if ([401, 403].includes(error.response?.status)) navigate("/admin");
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, t]);

  useEffect(() => {
    if (userPermissions.includes(36)) {
      fetchCustomers(statusFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleApprove = async (id) => {
    if (!hasPermission(38)) return showNotification("danger", t("customers.noPermissionAction"));
    setActioningId(id);
    try {
      await axios.put(`${CONSTANTS.baseRoute}/customers/${id}/approve`);
      setCustomers((prev) => prev.filter((c) => c.id !== id) || []);
      showNotification("success", t("customers.approveSuccess"));
    } catch (error) {
      showNotification("danger", error.response?.data?.error || t("customers.actionError"));
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async (id) => {
    if (!hasPermission(38)) return showNotification("danger", t("customers.noPermissionAction"));
    if (!window.confirm(t("customers.confirmReject"))) return;

    setActioningId(id);
    try {
      await axios.put(`${CONSTANTS.baseRoute}/customers/${id}/reject`);
      setCustomers((prev) => prev.filter((c) => c.id !== id) || []);
      showNotification("success", t("customers.rejectSuccess"));
    } catch (error) {
      showNotification("danger", error.response?.data?.error || t("customers.actionError"));
    } finally {
      setActioningId(null);
    }
  };

  const handleAddChange = (e) => {
    const { id, value } = e.target;
    setAddForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission(37)) return showNotification("danger", t("customers.noPermissionAction"));

    if (!addForm.name.trim() || !addForm.phone.trim() || !addForm.password) {
      showNotification("danger", t("customers.formRequired"));
      return;
    }

    setAddLoading(true);
    try {
      await axios.post(`${CONSTANTS.baseRoute}/customers`, {
        ...addForm,
        name: addForm.name.trim(),
        phone: addForm.phone.trim(),
        email: addForm.email.trim(),
      });
      showNotification("success", t("customers.createSuccess"));
      setAddForm({ name: "", phone: "", email: "", password: "" });
      setShowAddModal(false);
      fetchCustomers(statusFilter);
    } catch (error) {
      showNotification("danger", error.response?.data?.error || t("customers.createError"));
    } finally {
      setAddLoading(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { cls: "status-pending", text: t("customers.statusPending") },
      approved: { cls: "status-approved", text: t("customers.statusApproved") },
      rejected: { cls: "status-rejected", text: t("customers.statusRejected") },
    };
    const info = map[status] || { cls: "status-pending", text: status };
    return <span className={`status-pill ${info.cls}`}>{info.text}</span>;
  };

  const tabs = [
    { key: "approved", label: t("customers.tabApproved") },
    { key: "rejected", label: t("customers.tabRejected") },
    { key: "all", label: t("customers.tabAll") },
  ];

  if (loading && customers.length === 0) {
    return (
      <div className="admin-main p-4 d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("common.loading")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-main ps-2 pe-2 py-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex gap-2 align-items-center">
          <button className="btn admin-icon-btn" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button className="btn admin-icon-btn" onClick={() => navigate(+1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <h4 className="admin-page-title">{t("customers.title")}</h4>
        </div>
        {hasPermission(37) && (
          <button
            className="btn btn-success shadow-sm"
            onClick={() => setShowAddModal(true)}
          >
            <i className="bi bi-person-plus me-2"></i>
            {t("customers.addCustomer")}
          </button>
        )}
      </div>

      {message.text && (
        <div
          className={`alert alert-${message.type} shadow-sm mb-4 alert-dismissible fade show`}
          role="alert"
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage({ type: "", text: "" })}
          ></button>
        </div>
      )}

      <ul className="admin-tabs">
        {tabs.map((tab) => (
          <li key={tab.key}>
            <button
              type="button"
              className={`admin-tab ${statusFilter === tab.key ? "active" : ""}`}
              onClick={() => setStatusFilter(tab.key)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="admin-card overflow-hidden">
        <div
          className="admin-card-header d-flex justify-content-between align-items-center"
        >
          <span><i className="bi bi-person-check-fill me-2"></i>{t("customers.listTitle")}</span>
          <span className="badge bg-light text-dark fs-6">{customers.length}</span>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0 text-center align-middle">
              <thead className="table-light">
                <tr>
                  <th></th>
                  <th>{t("customers.name")}</th>
                  <th>{t("customers.phone")}</th>
                  <th>{t("customers.email")}</th>
                  <th>{t("customers.status")}</th>
                  <th>{t("customers.registeredAt")}</th>
                  <th className="text-end pe-4">{t("customers.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {customers.length > 0 ? (
                  customers.map((customer) => (
                    <tr
                      key={customer.id}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/admin/home/customers/${customer.id}`)
                      }
                    >
                      <td>
                        {customer.profile_image ? (
                          <img
                            src={`${CONSTANTS.baseRoute}${customer.profile_image}`}
                            alt={customer.name || "Customer"}
                            style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "50%" }}
                          />
                        ) : (
                          <i className="bi bi-person-circle fs-3 text-muted"></i>
                        )}
                      </td>
                      <td className="fw-medium">{customer.name || "—"}</td>
                      <td>{customer.phone || "—"}</td>
                      <td>{customer.email || "—"}</td>
                      <td>{statusBadge(customer.status)}</td>
                      <td>
                        {customer.created_at
                          ? new Date(customer.created_at).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="text-end pe-4">
                        {customer.status !== "approved" && (
                          <button
                            className="btn btn-sm btn-outline-success me-2 shadow-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApprove(customer.id);
                            }}
                            disabled={actioningId === customer.id}
                            title={t("customers.approve")}
                          >
                            <i className="bi bi-check-lg"></i>
                          </button>
                        )}
                        {customer.status !== "rejected" && (
                          <button
                            className="btn btn-sm btn-outline-warning me-2 shadow-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReject(customer.id);
                            }}
                            disabled={actioningId === customer.id}
                            title={t("customers.reject")}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="py-5 text-muted">
                      <i className="bi bi-person-slash display-4 mb-3 d-block text-muted"></i>
                      {t("customers.noCustomersFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowAddModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-person-plus me-2"></i>
                  {t("customers.addModalTitle")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddModal(false)}
                ></button>
              </div>
              <form onSubmit={handleAddSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      {t("customers.name")} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control shadow-sm"
                      placeholder={t("customers.namePlaceholder")}
                      id="name"
                      value={addForm.name}
                      onChange={handleAddChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      {t("customers.phone")} <span className="text-danger">*</span>
                    </label>
                    <div className="input-group shadow-sm">
                      <span className="input-group-text">+993</span>
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="61234567"
                        id="phone"
                        value={addForm.phone}
                        onChange={handleAddChange}
                        maxLength={8}
                        pattern="[0-9]{8}"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">{t("customers.email")}</label>
                    <input
                      type="email"
                      className="form-control shadow-sm"
                      id="email"
                      value={addForm.email}
                      onChange={handleAddChange}
                    />
                  </div>

                  <div className="mb-1">
                    <label className="form-label fw-bold">
                      {t("customers.password")} <span className="text-danger">*</span>
                    </label>
                    <div className="input-group shadow-sm">
                      <input
                        type={showAddPassword ? "text" : "password"}
                        className="form-control"
                        id="password"
                        value={addForm.password}
                        onChange={handleAddChange}
                        minLength={6}
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowAddPassword(!showAddPassword)}
                      >
                        <i className={`bi ${showAddPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowAddModal(false)}
                  >
                    {t("common.cancel")}
                  </button>
                  <button type="submit" className="btn btn-success" disabled={addLoading}>
                    {addLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t("common.loading")}
                      </>
                    ) : (
                      t("common.add")
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
